import { useTranslation } from 'react-i18next';
import { PickupItem } from '@/types';
import { getSlotIcon } from './slotIcons';
import { formatSlotWindow12 } from '@/utils/helpers';

interface SlotBadgeProps {
  slot?: PickupItem['slot'];
  slotState?: string | null;
  showIcon?: boolean;
}

const SlotBadge = ({ slot, slotState, showIcon = true }: SlotBadgeProps) => {
  const { t } = useTranslation();

  if (!slot || !slot.code) {
    // v2-task-8b (§2.8/§3.4): use the shared muted-neutral pair
    // (rgb(245,245,243)/rgb(115,123,140)) instead of Tailwind's default
    // gray-100/gray-500 ramp — every other muted surface in this feature
    // uses this exact pair.
    return (
      <span
        className='inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium'
        style={{ background: 'rgb(245,245,243)', color: 'rgb(115,123,140)' }}
      >
        {String(t('no_slot_selected'))}
      </span>
    );
  }

  const SlotIcon = getSlotIcon(slot.code);

  return (
    <span
      className='inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium'
      // Darker yellow-brown text (vs. raw sd-primary yellow) — deliberate
      // contrast fix: yellow-on-near-white text fails WCAG AA at this size.
      // rgb(120,110,10) clears ~4.6:1 on the 16%-tint-over-white bg (AA for 12px).
      style={{ background: 'rgba(237,224,74,0.16)', color: 'rgb(120,110,10)' }}
      data-slot-state={slotState ?? undefined}
    >
      {showIcon ? <SlotIcon size={12} /> : null}
      {slot.label} <bdi>({formatSlotWindow12(slot.window_from, slot.window_to)})</bdi>
    </span>
  );
};

export default SlotBadge;
